import React, {useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid2,
  TextField,
  Typography
} from '@mui/material';
import {ArrowBack, ErrorOutline, Flight, FlightTakeoff, History, Search} from '@mui/icons-material';
import {t} from 'ttag';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {DateTime} from 'luxon';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {useGetFlightsMutation} from '../../store/apis/ccs';
import {styles} from '@invoice-system';
import useFadeNavigate from './useFadeNavigate';
import FlightResultCard from './FlightResultCard';

const FlightLoadingCard = () => (
  <Card variant='outlined' sx={{mt: 3, borderRadius: 2}}>
    <CardContent sx={{p: 3}}>
      <Box sx={{position: 'relative', height: 96, display: 'flex', alignItems: 'center', mb: 1.5}}>
        {/* Dashed flight path */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '8%',
          right: '8%',
          height: '1px',
          background: 'repeating-linear-gradient(to right, rgba(63,81,181,0.3) 0, rgba(63,81,181,0.3) 7px, transparent 7px, transparent 15px)',
          transform: 'translateY(-50%)',
        }} />

        {/* Origin dot */}
        <Box sx={{
          position: 'absolute', left: '8%', top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 10, height: 10, borderRadius: '50%',
          bgcolor: 'primary.main', opacity: 0.35,
        }} />

        {/* Destination dot */}
        <Box sx={{
          position: 'absolute', right: '8%', top: '50%',
          transform: 'translate(50%, -50%)',
          width: 10, height: 10, borderRadius: '50%',
          bgcolor: 'primary.main', opacity: 0.35,
        }} />

        {/* Animated plane */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'primary.main',
          animation: 'planeFly 2.6s cubic-bezier(0.45, 0, 0.55, 1) infinite',
          '@keyframes planeFly': {
            '0%':   {left: '5%',  marginTop:  '10px', opacity: 0},
            '8%':   {              opacity: 1},
            '30%':  {              marginTop: '-16px'},
            '55%':  {left: '50%', marginTop:   '4px'},
            '78%':  {              marginTop: '-10px'},
            '92%':  {              opacity: 1},
            '100%': {left: '93%', marginTop:   '8px', opacity: 0},
          },
        }}>
          <FlightTakeoff sx={{fontSize: '2.2rem'}} />
        </Box>
      </Box>

      <Typography
        variant='body2'
        color='text.secondary'
        textAlign='center'
        sx={{
          animation: 'textPulse 1.5s ease-in-out infinite',
          '@keyframes textPulse': {
            '0%, 100%': {opacity: 1},
            '50%': {opacity: 0.4},
          },
        }}
      >
        {t`Searching for flight...`}
      </Typography>
    </CardContent>
  </Card>
);

const FlightErrorCard = () => (
  <Card
    variant='outlined'
    sx={{mt: 3, borderRadius: 2, borderColor: 'error.main', borderTopWidth: 3}}
  >
    <CardContent sx={{p: 3, textAlign: 'center'}}>
      <Box
        sx={{
          bgcolor: 'error.50',
          borderRadius: '50%',
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}
      >
        <ErrorOutline sx={{fontSize: 32, color: 'error.main'}} />
      </Box>
      <Typography variant='h6' fontWeight='bold' color='error.main' gutterBottom>
        {t`Flight Not Found`}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {t`No flight matched the provided details. Please check the carrier, flight number, date, and origin, then try again.`}
      </Typography>
    </CardContent>
  </Card>
);

const validationSchema = yup.object().shape({
  carrier: yup.string().required(t`Carrier is required`),
  flightNumber: yup.string().required(t`Flight number is required`),
  date: yup.string().required(t`Date is required`),
  origin: yup.string().required(t`Origin is required`),
});

const SEARCH_STORAGE_KEY = 'flightDeclarationLastSearch';
const RECENTS_KEY = 'flightDeclarationRecents';

type RecentSearch = {carrier: string; flightNumber: string; date: string; origin: string};

const readSavedSearch = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SEARCH_STORAGE_KEY) ?? 'null');
  } catch {
    return null;
  }
};

const loadRecents = (): RecentSearch[] => {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]'); } catch { return []; }
};

const saveRecent = (s: RecentSearch) => {
  const deduped = loadRecents().filter(
    (r) => !(r.carrier === s.carrier && r.flightNumber === s.flightNumber && r.date === s.date && r.origin === s.origin)
  );
  localStorage.setItem(RECENTS_KEY, JSON.stringify([s, ...deduped].slice(0, 5)));
};

const FlightDeclarationPage = () => {
  const navigate = useFadeNavigate();
  const [dateOpen, setDateOpen] = useState(false);
  const datePickerContainerRef = useRef<HTMLDivElement>(null);
  const pendingFocusTarget = useRef<HTMLElement | null>(null);
  // True when mousedown landed on the date input text area (not the calendar icon button).
  // Used to suppress ClickAwayListener's close so the calendar stays open while typing.
  const suppressClose = useRef(false);

  useEffect(() => {
    if (!dateOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const insidePicker = datePickerContainerRef.current?.contains(target) ?? false;
      if (insidePicker) {
        // Suppress close unless the user clicked a button (e.g. the calendar icon), which
        // should still act as a toggle.
        if (!target.closest('button')) suppressClose.current = true;
      } else if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        pendingFocusTarget.current = target;
      }
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [dateOpen]);

  const [lastSearch, setLastSearch] = useState<RecentSearch | null>(() => {
    const saved = readSavedSearch();
    return saved ? {carrier: saved.carrier, flightNumber: saved.flightNumber, date: saved.date, origin: saved.origin} : null;
  });

  const [getFlights, {data: fetchedFlights, isLoading, error, reset: resetQuery}] = useGetFlightsMutation();
  const [restoredFlights, setRestoredFlights] = useState<any[] | null>(() => readSavedSearch()?.flights ?? null);
  const [recents, setRecents] = useState<RecentSearch[]>(() => loadRecents());

  // Whichever is available — prefer a fresh fetch, fall back to the restored ones
  const flights = fetchedFlights ?? restoredFlights;

  // Persist the flight results and update recents whenever a new search succeeds
  useEffect(() => {
    if (fetchedFlights && lastSearch) {
      try {
        sessionStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify({...lastSearch, flights: fetchedFlights}));
      } catch {}
      saveRecent(lastSearch);
      setRecents(loadRecents());
    }
  }, [fetchedFlights]);

  const {control, handleSubmit, reset} = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: readSavedSearch() ?? {
      carrier: '',
      flightNumber: '',
      date: DateTime.now().toFormat('yyyy-MM-dd'),
      origin: '',
    }
  });

  const handleSubmitForm = handleSubmit(
    (data) => {
      resetQuery();
      setRestoredFlights(null);
      const carrier = data.carrier.toUpperCase();
      const flightNumber = data.flightNumber;
      const origin = data.origin.toUpperCase();
      setLastSearch({carrier, flightNumber, date: data.date, origin});
      getFlights({carrier, flightNumber, date: data.date, origin});
    },
    (err) => console.error(err)
  );

  const applyRecent = (r: RecentSearch) => {
    reset({carrier: r.carrier, flightNumber: r.flightNumber, date: r.date, origin: r.origin});
    resetQuery();
    setRestoredFlights(null);
    setLastSearch(r);
    getFlights({carrier: r.carrier, flightNumber: r.flightNumber, date: r.date, origin: r.origin});
  };

  return (
    <Card elevation={3}>
      <CardHeader
        title={
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <FlightTakeoff sx={{color: 'text.primary'}} />
            <Typography sx={styles.title}>{t`Search Flight`}</Typography>
          </Box>
        }
        subheader={
          <Typography sx={styles.subHeader} variant='subtitle2'>
            {t`Enter the flight details to retrieve flight history and passenger information.`}
          </Typography>
        }
      />
      <Divider />
      <CardContent sx={{py: 4}}>
        <Grid2 container spacing={2}>
          <Grid2 size={{xs: 12, sm: 3}}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <Controller
                name='date'
                control={control}
                render={({field, fieldState: {error: fieldError}}) => (
                  <div ref={datePickerContainerRef}>
                    <DatePicker
                      label={t`Flight Date`}
                      value={field.value ? DateTime.fromFormat(field.value, 'yyyy-MM-dd') : null}
                      onChange={(val: DateTime | null) => field.onChange(val?.toFormat('yyyy-MM-dd') ?? '')}
                      inputFormat='dd-MM-yyyy'
                      open={dateOpen}
                      onOpen={() => {
                        pendingFocusTarget.current = null;
                        suppressClose.current = false;
                        setDateOpen(true);
                      }}
                      onClose={() => {
                        if (suppressClose.current) {
                          // User clicked the date text field — keep the calendar open so they
                          // can type while the picker is visible.
                          suppressClose.current = false;
                          return;
                        }
                        suppressClose.current = false;
                        setDateOpen(false);
                        const target = pendingFocusTarget.current;
                        pendingFocusTarget.current = null;
                        if (target) {
                          const dateInput = datePickerContainerRef.current?.querySelector<HTMLInputElement>('input');
                          if (dateInput) {
                            const saved = dateInput.focus.bind(dateInput);
                            (dateInput as any).focus = () => {};
                            setTimeout(() => {
                              (dateInput as any).focus = saved;
                              target.focus();
                            }, 100);
                          } else {
                            setTimeout(() => target.focus(), 100);
                          }
                        }
                      }}
                      PaperProps={{onMouseDown: (e) => e.preventDefault()}}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size='small'
                          fullWidth
                          error={!!fieldError}
                          helperText={fieldError ? fieldError.message : t`Select the flight date`}
                          onClick={() => setDateOpen(true)}
                        />
                      )}
                    />
                  </div>
                )}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2 size={{xs: 12, sm: 3}}>
            <Controller
              name='carrier'
              control={control}
              render={({field, fieldState: {error: fieldError}}) => (
                <TextField
                  {...field}
                  label={t`Carrier`}
                  placeholder='S4'
                  variant='outlined'
                  fullWidth
                  size='small'
                  error={!!fieldError}
                  helperText={fieldError ? fieldError.message : t`Airline carrier code (e.g. S4)`}
                  inputProps={{style: {textTransform: 'uppercase'}}}
                />
              )}
            />
          </Grid2>

          <Grid2 size={{xs: 12, sm: 3}}>
            <Controller
              name='flightNumber'
              control={control}
              render={({field, fieldState: {error: fieldError}}) => (
                <TextField
                  {...field}
                  label={t`Flight Number`}
                  placeholder='201'
                  variant='outlined'
                  fullWidth
                  size='small'
                  error={!!fieldError}
                  helperText={fieldError ? fieldError.message : t`Flight number (e.g. 201)`}
                />
              )}
            />
          </Grid2>

          <Grid2 size={{xs: 12, sm: 3}}>
            <Controller
              name='origin'
              control={control}
              render={({field, fieldState: {error: fieldError}}) => (
                <TextField
                  {...field}
                  label={t`Origin`}
                  placeholder='PDL'
                  variant='outlined'
                  fullWidth
                  size='small'
                  error={!!fieldError}
                  helperText={fieldError ? fieldError.message : t`Departure airport (e.g. PDL)`}
                  inputProps={{style: {textTransform: 'uppercase'}}}
                />
              )}
            />
          </Grid2>

        </Grid2>

        <Box sx={{display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mt: 2.5}}>
          {recents.length > 0 ? (
            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 1}}>
                <History sx={{fontSize: '0.9rem', color: 'text.disabled'}} />
                <Typography variant='caption' color='text.secondary' sx={{fontWeight: 700, letterSpacing: '0.08em'}}>
                  {t`RECENT SEARCHES`}
                </Typography>
              </Box>
              <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                {recents.map((r, i) => (
                  <Chip
                    key={i}
                    icon={<FlightTakeoff sx={{fontSize: '0.8rem !important'}} />}
                    label={`${r.carrier}${r.flightNumber} · ${r.origin} · ${DateTime.fromFormat(r.date, 'yyyy-MM-dd').toFormat('dd MMM yyyy')}`}
                    size='small'
                    variant='outlined'
                    onClick={() => applyRecent(r)}
                    disabled={isLoading}
                    sx={{fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer', '&:hover': {borderColor: 'primary.main', color: 'primary.main'}}}
                  />
                ))}
              </Box>
            </Box>
          ) : <Box sx={{flex: 1}} />}
          <Button
            variant='contained'
            color='primary'
            startIcon={<Search />}
            onClick={handleSubmitForm}
            disabled={isLoading}
            sx={{flexShrink: 0}}
          >
            {isLoading ? t`Searching...` : t`Search Flight`}
          </Button>
        </Box>

        {isLoading && <FlightLoadingCard />}

        {error && !isLoading && <FlightErrorCard />}

        {flights && lastSearch && (
          <Box sx={{mt: 1}}>
            {flights.length > 1 && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5}}>
                <Flight sx={{fontSize: '1rem', color: 'text.secondary'}} />
                <Typography variant='body2' color='text.secondary'>
                  {t`Found`} <strong>{flights.length}</strong> {t`legs for`} <strong>{lastSearch.carrier.toUpperCase()}{lastSearch.flightNumber}</strong> {t`— select the leg to issue a declaration for`}
                </Typography>
              </Box>
            )}
            {flights.map((flight) => (
              <FlightResultCard
                key={flight.flightId}
                flight={flight}
                searchDate={lastSearch!.date}
                carrier={lastSearch!.carrier}
                flightNumber={lastSearch!.flightNumber}
                onContinue={() => {
                  navigate(
                    '/declarations/flight/passengers',
                    {
                      state: {
                        flight,
                        carrier: lastSearch!.carrier,
                        flightNumber: lastSearch!.flightNumber,
                        date: lastSearch!.date,
                        origin: flight.origin,
                      }
                    }
                  );
                }}
              />
            ))}
          </Box>
        )}
      </CardContent>
      <Divider />
      <CardActions sx={{m: 1}}>
        <Button variant='text' startIcon={<ArrowBack />} sx={{color: 'text.secondary'}} onClick={() => navigate('/declarations')}>
          {t`Back`}
        </Button>
      </CardActions>
    </Card>
  );
};

export default FlightDeclarationPage;
